import { useEffect } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
  BodyText,
  Button,
  HStack,
  Modal,
  theme,
  useResponsive,
} from '@sg/garnish';

import { CustomizationModalContentContainer } from '../CustomizationModalContentContainer';
import { CustomizationModalHeading } from '../CustomizationModalHeading';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationConfirmExitModal = (props: Props) => {
  const { isVisible, target, size = 'md', onConfirm, onCancel, onShow } = props;

  const { formatMessage } = useIntl();
  const { match } = useResponsive();

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isVisible) return;

    onShow?.();
  }, [isVisible, onShow]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Modal
      visible={isVisible}
      size="medium"
      accessibilityLabel={formatMessage(messages.modalLabel)}
      onRequestClose={onCancel}
    >
      <CustomizationModalContentContainer size={size}>
        <CustomizationModalHeading>
          <FormattedMessage {...messages.heading} values={{ target }} />
        </CustomizationModalHeading>

        <BodyText sizeMatch={['16']}>
          <FormattedMessage {...messages.body} />
        </BodyText>

        <SafeAreaView edges={match([['bottom'], undefined])}>
          <HStack
            itemsPerRow={2}
            gap={theme.spacing['4']}
            style={styles.ctaContainer}
          >
            <Button
              size="large-wide"
              palette="secondary"
              onPress={onConfirm}
              style={styles.cta}
            >
              <FormattedMessage {...messages.returnTo} values={{ target }} />
            </Button>

            <Button
              size="large-wide"
              palette="primary"
              onPress={onCancel}
              style={styles.cta}
            >
              <FormattedMessage {...messages.continueCustomization} />
            </Button>
          </HStack>
        </SafeAreaView>
      </CustomizationModalContentContainer>
    </Modal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  heading: {
    defaultMessage: 'Are you sure you want to return to {target}?',
    description: 'Customization | Confirm exit modal | Heading',
  },
  body: {
    defaultMessage: 'You will lose any customizations you’ve made.',
    description: 'Customization | Confirm exit modal | Body',
  },
  modalLabel: {
    defaultMessage: 'Confirm exit modal',
    description: 'Customization | Confirm exit modal | Label',
  },
  returnTo: {
    defaultMessage: 'Yes, return to {target}',
    description: 'Customization | Confirm exit modal | Return button label',
  },
  continueCustomization: {
    defaultMessage: 'No, continue to customize',
    description:
      'Customization | Confirm exit modal | Continue customization button label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  ctaContainer: {
    paddingTop: theme.spacing['2'],
  },
  cta: {
    paddingHorizontal: theme.spacing['1'],
    minHeight: 72,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isVisible: boolean;
  target: string;
  size?: 'md' | 'lg';
  onConfirm: () => void;
  onCancel: () => void;
  onShow?: () => void;
};
